import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  QwantaProvider,
  getMetaQwanta,
  getJsxQwanta,
  useQwanyx,
  QwantaComponent,
  componentRegistry,
  registerCardComponent
} from "qwanyx";
import JsxFileHandler from './jsxFileHandler';

// Self-contained Card that can be used directly
export const Card = ({ _id, di, ed, pa, useGlobalContext = false }) => {


  // Track initial props
  const initialPropsRef = useRef({ _id, di, ed, pa });

  // Track if we've initialized from selected card yet
  const [initializedFromContext, setInitializedFromContext] = useState(false);

  // Track actual card data directly as state
  const [cardData, setCardData] = useState(initialPropsRef.current);

  // Add loading state to control visibility
  const [isLoading, setIsLoading] = useState(true);

  // Get selectedCard from context when useGlobalContext is true
  const { selectedCard } = useQwanyx();

  // Debug context readiness
  useEffect(() => {
    if (useGlobalContext) {
      console.log("Context state:", {
        selectedCard: selectedCard,
        initializedFromContext
      });
    }
  }, [useGlobalContext, selectedCard, initializedFromContext]);

  // Update cardData whenever props or selectedCard changes
  useEffect(() => {
    // Set loading state to true whenever data changes
    setIsLoading(true);

    // Case 1: Using global context and we have a selected card
    if (useGlobalContext && selectedCard && selectedCard._id) {
      console.log("🔄 Updating from context:", selectedCard);
      setCardData({
        _id: selectedCard._id,
        di: selectedCard.di,
        ed: selectedCard.ed,
        pa: selectedCard.pa
      });

      // Mark that we've initialized from context
      if (!initializedFromContext) {
        setInitializedFromContext(true);
      }
    }
    // Case 2: Using direct props OR context isn't ready yet
    else if (!useGlobalContext || !initializedFromContext) {
      console.log("🔄 Updating from props:", { _id, di, ed, pa });
      setCardData({ _id, di, ed, pa });
    }
    // Case 3: Using global context but selectedCard is null and we've already initialized
    // In this case, we don't update cardData to prevent flickering back to props
  }, [
    // Props dependencies
    _id, di, ed, pa,
    // Context dependencies
    useGlobalContext,
    selectedCard?._id,
    selectedCard?.di,
    selectedCard?.ed,
    selectedCard?.pa,
    initializedFromContext
  ]);

  const containerRef = useRef(null);
  const [metaQwanta, setMetaQwanta] = useState([]);
  const [jsxQwanta, setJsxQwanta] = useState(null);
  const [compiledJsx, setCompiledJsx] = useState(null);
  const [providerReady, setProviderReady] = useState(false);
  const [renderMode, setRenderMode] = useState('meta'); // 'meta', 'jsx', or 'both'
  const [debugMode, setDebugMode] = useState(false);

  // ✅ SAFETY NET: Warn if `di` or `ed` are null, but KEEP the provider!
  const missingData = cardData.di === null || cardData.ed === null;

  // 1️⃣ Scroll Reset when `_id` changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [cardData._id]);

  // 2️⃣ Update `QwantaProvider` first before fetching components
  useEffect(() => {
    console.log("⚡ Card data changed, resetting provider:", cardData);
    // Reset metaQwanta to force a complete refresh
    setMetaQwanta([]);
    setJsxQwanta(null);
    setCompiledJsx(null);
    setIsLoading(true);

    setProviderReady(false);
    let timeout = setTimeout(() => setProviderReady(true), 50);
    return () => clearTimeout(timeout);
  }, [cardData._id, cardData.di, cardData.pa]);

  // Create JSX File Handler instance with component registry
  const jsxHandler = useMemo(() => {
    // Create a unified component registry with our Card component to prevent circular dependency
    const fullRegistry = {
      ...componentRegistry,
      Card: Card // Add Card to prevent circular dependency issues
    };

    return new JsxFileHandler(fullRegistry);
  }, []);

  // Process JSX string to React element using our handler
  const processJsxString = (jsxString) => {
    if (!jsxString || typeof jsxString !== 'string') {
      console.error('Invalid JSX string provided', jsxString);
      return null;
    }

    try {
      // Process the full JSX string with our handler
      const { element, success, error } = jsxHandler.processJsx(jsxString);

      if (!success) {
        console.error('Error processing JSX:', error);
      } else {
        console.log('✅ JSX processed successfully');
      }

      return element;
    } catch (error) {
      console.error('Error processing JSX:', error);
      return (
        <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
          Error processing JSX: {error.message}
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{error.stack}</pre>
        </div>
      );
    }
  };

  // 3️⃣ Fetch Components AFTER `QwantaProvider` is ready
  useEffect(() => {
    if (!providerReady || missingData) return; // Prevent fetch if missing data

    const fetchCardData = async () => {
      try {
        // Use _id when pa is empty
        const editorConfigId = cardData.pa || cardData._id;

        if (!editorConfigId) {
          console.error("❌ Both pa and _id are empty, cannot fetch data.");
          setIsLoading(false);
          return;
        }

        console.log(`🔄 Fetching data for: ${editorConfigId}, di: ${cardData.di}`);

        // Fetch both data sources in parallel
        const [persistedMetaqwanta, persistedJsxqwanta] = await Promise.all([
          getMetaQwanta(editorConfigId, cardData.di),
          getJsxQwanta(editorConfigId, cardData.di)
        ]);

        // Process metaQwanta
        if (Array.isArray(persistedMetaqwanta)) {
          console.log(`✅ Fetched ${persistedMetaqwanta.length} metaQwanta components`);
          setMetaQwanta(persistedMetaqwanta);
        } else {
          console.error("❌ Unexpected response format from getMetaQwanta:", persistedMetaqwanta);
        }

        // Process JSX string
        if (persistedJsxqwanta && typeof persistedJsxqwanta === 'string') {
          console.log(`✅ Fetched JSX string (${persistedJsxqwanta.length} chars)`);
          setJsxQwanta(persistedJsxqwanta);

          // Process and compile the JSX string
          try {
            console.log('Processing JSX string...');
            const compiled = processJsxString(persistedJsxqwanta);
            setCompiledJsx(compiled);

            // Set render mode based on what we have
            if (Array.isArray(persistedMetaqwanta) && persistedMetaqwanta.length > 0) {
              if (compiled) {
                // User can choose which to prefer, default to JSX if both are available
                const preferJsx = true; // This could be a prop or configured setting
                setRenderMode(preferJsx ? 'jsx' : 'both');
                console.log(`✅ Using ${preferJsx ? 'JSX only' : 'both Meta+JSX'} rendering mode`);
              } else {
                setRenderMode('meta');
                console.log('✅ Using Meta rendering mode (JSX not available)');
              }
            } else if (compiled) {
              setRenderMode('jsx');
              console.log('✅ Using JSX rendering mode (Meta not available)');
            } else {
              console.warn('⚠️ No valid rendering content available');
            }
          } catch (error) {
            console.error('Failed to process JSX:', error);

            if (Array.isArray(persistedMetaqwanta) && persistedMetaqwanta.length > 0) {
              setRenderMode('meta'); // Fallback to metaQwanta if JSX fails
              console.log('⚠️ Falling back to Meta rendering mode due to JSX error');
            }
          }
        } else {
          console.log("⚠️ No JSX string found or it's invalid");
          setRenderMode('meta'); // Default to metaQwanta rendering
        }

        // Allow a brief moment for React to process the updates
        setTimeout(() => setIsLoading(false), 100);
      } catch (error) {
        console.error("❌ Error fetching card data:", error);
        setIsLoading(false);
      }
    };

    fetchCardData();
  }, [providerReady, cardData._id, cardData.di, cardData.pa, missingData]);

  // Only show "No card selected" when we've tried to initialize from context but got nothing
  if (useGlobalContext && initializedFromContext && (!selectedCard || !selectedCard._id)) {
    return <div>No card selected</div>;
  }

  return (
    <QwantaProvider
      key={`${cardData._id}-${cardData.di}`}
      _id={cardData._id}
      di={cardData.di}
      ed={cardData.ed}
      pa={cardData.pa}
      embedEditor={false}
      qwantaType="card"
    >
      <div
        ref={containerRef}
        style={{
          flexGrow: 1,
          width: "100%",
          position: "relative",
          opacity: isLoading ? 0 : 1, // Hide while loading
          transition: "opacity 0.5s ease", // Fade in when ready
        }}
      >
        {/* Optional loading indicator */}
        {isLoading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10
          }}>
            <div>Loading...</div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", backgroundColor: 'transparent' }}>
          {/* Debug Mode Toggle 
          
          <div style={{ padding: '5px', textAlign: 'right' }}>
            <button 
              onClick={() => setDebugMode(!debugMode)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: debugMode ? '#f0f0f0' : 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {debugMode ? 'Exit Debug' : 'Debug'}
            </button>
          </div>
          */}

          {missingData ? (
            <div style={{ padding: 20, textAlign: "center", color: "red" }}>
              <h3>Missing required data</h3>
              <p>Please ensure both `di` and `pa` are provided.</p>
            </div>
          ) : debugMode ? (
            /* Debug Mode View */
            <div style={{ padding: 15, fontSize: '14px', border: '1px solid #ddd', backgroundColor: '#f9f9f9' }}>
              <h3>Card Debug Info</h3>
              <div style={{ marginBottom: '10px' }}>
                <strong>Render Mode:</strong> {renderMode}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>MetaQwanta Components:</strong> {metaQwanta?.length || 0}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>JSX Available:</strong> {jsxQwanta ? 'Yes' : 'No'}
                ({jsxQwanta?.length || 0} chars)
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>JSX Compiled Successfully:</strong> {compiledJsx ? 'Yes' : 'No'}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  onClick={() => setRenderMode('meta')}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: renderMode === 'meta' ? '#e0e0e0' : 'white',
                    border: '1px solid #ccc'
                  }}
                >
                  Use MetaQwanta
                </button>
                <button
                  onClick={() => setRenderMode('jsx')}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: renderMode === 'jsx' ? '#e0e0e0' : 'white',
                    border: '1px solid #ccc'
                  }}
                  disabled={!compiledJsx}
                >
                  Use JSX
                </button>
                <button
                  onClick={() => setRenderMode('both')}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: renderMode === 'both' ? '#e0e0e0' : 'white',
                    border: '1px solid #ccc'
                  }}
                  disabled={!compiledJsx || !metaQwanta.length}
                >
                  Use Both
                </button>
              </div>

              {jsxQwanta && (
                <div style={{ marginTop: '15px' }}>
                  <h4>JSX Source Preview:</h4>
                  <pre style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    padding: '10px',
                    border: '1px solid #ddd',
                    fontSize: '12px'
                  }}>
                    {jsxQwanta.substring(0, 1000)}{jsxQwanta.length > 1000 ? '...' : ''}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Normal Render Mode */}
              {/* Render content based on the render mode */}
              {(renderMode === 'meta' || renderMode === 'both') && providerReady && metaQwanta.length > 0 && (
                <div className="meta-qwanta-container">
                  {metaQwanta
                    .filter((component) => component)
                    .map((component, index) => (
                      <QwantaComponent
                        key={`root-${index}`}
                        component={component}
                        index={index}
                        parentId="root"
                      />
                    ))}
                </div>
              )}

              {/* Render the compiled JSX if available */}
              {(renderMode === 'jsx' || renderMode === 'both') && compiledJsx && (
                <div >
                  {compiledJsx}
                </div>
              )}

              {/* Show a message if no content is available */}
              {!isLoading && renderMode !== 'meta' && renderMode !== 'jsx' && renderMode !== 'both' && (
                <div style={{ padding: 20, textAlign: "center" }}>
                  <p>No content available for this card.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </QwantaProvider>
  );
};

// Register the Card component
registerCardComponent(Card);

export default Card;